import type { TFunction } from 'i18next';
import { VendorPlan } from '../../superadmin/our-vendors/models/vendor.model';
import { readLimit } from '../../../components/design-system/plan/plan-limits';

/**
 * The plan ladder as a vendor sees it (docs/pricing-v2.md, API repo):
 * the public plans in admin order, plus the vendor's own plan even when it is
 * not offered any more ("you are here" belongs next to "where you could go").
 */
export function visibleLadder(plans: VendorPlan[], mySlug?: string, myPlan?: VendorPlan | null): VendorPlan[] {
  const list = plans.filter((p) => p.isPublic !== false || p.slug === mySlug);
  if (myPlan?.slug && !list.some((p) => p.slug === myPlan.slug)) list.push(myPlan);
  return list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** Only the plans someone can move to: public, not a trial, not free. */
export const purchasable = (plans: VendorPlan[]): VendorPlan[] =>
  plans.filter((p) => p.isPublic !== false && p.pricingType !== 'trial' && p.pricingType !== 'free' && p.slug !== 'free');

export type Interval = 'month' | 'year';

const symbolOf = (currency?: string) => {
  const c = (currency || 'usd').toLowerCase();
  if (c === 'usd') return '$';
  if (c === 'eur') return '€';
  if (c === 'gbp') return '£';
  return `${c.toUpperCase()} `;
};

export const money = (amount: number, currency?: string): string =>
  `${symbolOf(currency)}${String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

/**
 * What a plan costs per month on the chosen interval: the monthly price, or
 * the annual price spread over twelve months. Null for a plan without one.
 */
export function monthlyPrice(plan: VendorPlan, interval: Interval): number | null {
  const monthly = Number(plan.price) || 0;
  if (monthly <= 0) return null;
  if (interval === 'year') {
    const annual = Number(plan.annualPrice) || monthly * 12;
    return annual / 12;
  }
  return monthly;
}

/** Months an annual price saves over paying monthly, rounded (Visualizer: 2). */
export function monthsSaved(plan: VendorPlan): number {
  const monthly = Number(plan.price) || 0;
  const annual = Number(plan.annualPrice) || 0;
  if (monthly <= 0 || annual <= 0) return 0;
  return Math.max(0, Math.round((monthly * 12 - annual) / monthly));
}

const n = (v: number) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * The few lines that tell the plans apart. The ladder is fenced by catalog
 * size and traffic, so those come first; then what the plan adds.
 * `plan.highlights`, when an admin wrote them, win.
 */
export function planHighlights(plan: VendorPlan, t: TFunction): string[] {
  if (plan.highlights?.length) return plan.highlights;
  const lim = (k: string) => readLimit((plan.limits as unknown as Record<string, unknown>)?.[k]);
  const f = plan.features || ({} as VendorPlan['features']);
  const out: string[] = [];
  const count = (key: string, v: number) =>
    v < 0 ? t(`PRICING.HIGHLIGHT.${key}_UNLIMITED`) : t(`PRICING.HIGHLIGHT.${key}`, { count: v, n: n(v) });

  out.push(t('PRICING.HIGHLIGHT.VISUALIZER'));
  if (plan.pricingType === 'custom') {
    // Sales-led: the volumes are whatever the contract buys, never "unlimited".
    out.push(t('PRICING.HIGHLIGHT.VOLUMES'));
    out.push(t('PRICING.HIGHLIGHT.SITES'));
    if (f.shopperChat) out.push(t('PRICING.HIGHLIGHT.ASSISTANT'));
    out.push(t('PRICING.HIGHLIGHT.MARKET'));
    out.push(t('PRICING.HIGHLIGHT.INTEGRATION'));
    out.push(t('PRICING.HIGHLIGHT.SUCCESS'));
    return out;
  }
  out.push(count('PRODUCTS', lim('maxProducts')));
  out.push(count('ROOMS', lim('maxRoomAnalysesPerMonth')));
  out.push(count('SEATS', lim('maxSeats')));
  if (f.shopperChat) out.push(t('PRICING.HIGHLIGHT.ASSISTANT'));
  if (f.emailAutomation) out.push(t('PRICING.HIGHLIGHT.EMAIL'));
  if (lim('maxKeywordRows') < 0) out.push(t('PRICING.HIGHLIGHT.MARKET'));
  const credits = lim('monthlyCredits');
  if (credits !== 0) out.push(count('CREDITS', credits));
  return out;
}

/** A vendor's offer as plans/my-plan returns it (docs/pricing-v2.md, Offers). */
export interface VendorOfferView {
  live?: boolean;
  currency?: string;
  integrationFee?: number | null;
  items: { plan: string; planName?: string; planSlug?: string; monthlyPrice: number; annualPrice: number | null }[];
}

export type PricedPlan = VendorPlan & { offered?: boolean; onRequest?: boolean };

/**
 * The ladder at this vendor's prices. Plans in its offer carry the offered
 * price (and become payable, whatever their own type); other sold plans read
 * "From" their list price and are priced on request. Without an offer, list
 * prices apply as they are.
 */
export function applyOffer(plans: VendorPlan[], offer?: VendorOfferView | null): PricedPlan[] {
  if (!offer || offer.live === false || !offer.items?.length) return plans;
  const byPlan = new Map(offer.items.map((i) => [String(i.plan), i]));
  return plans.map((p) => {
    const item = byPlan.get(String(p._id));
    if (item) {
      return {
        ...p,
        offered: true,
        pricingType: 'paid',
        price: item.monthlyPrice,
        annualPrice: item.annualPrice ?? item.monthlyPrice * 12,
        currency: offer.currency || p.currency,
        ...(offer.integrationFee !== null && offer.integrationFee !== undefined ? { integrationFee: offer.integrationFee } : {}),
      };
    }
    if (p.pricingType === 'paid') return { ...p, onRequest: true };
    return p;
  });
}
